import { deserializeArray, plainToClass } from 'class-transformer';
import { DataSourceField } from '../../shared/model/data-source';
import { mutateData } from '../../shared/util/data-source-util';
import { DeviceType } from '../model/device-type';
import { GPSPhantomFrame } from '../model/gps-phantom-frame';
import { AdditionalInfoImpl } from '../model/status/additional-info-impl';
import { CAN1Status } from '../model/status/can1-status';
import { CAN2Status } from '../model/status/can2-status';
import { CAN3Status } from '../model/status/can3-status';
import { CustomCANStatus } from '../model/status/custom-can-status';
import { GPSStatus } from '../model/status/gps-status';
import { LcdStatus } from '../model/status/lcd-status';
import { LedStatus } from '../model/status/led-status';
import { NVRStatus } from '../model/status/nvr-status';
import { OBUStatus } from '../model/status/obu-status';
import { SilStatus } from '../model/status/sil-status';
import { TicStatus } from '../model/status/tic-status';
import { VicomStatus } from '../model/status/vicom-status';
import { DeviceSettingsType } from '../model/device-settings-type';
import { BackLightColorSetting } from '../model/back-light-color-setting';
import { Type } from '@angular/compiler/src/core';
import { ClassType } from 'class-transformer/ClassTransformer';
import { BackLightTemperatureSetting } from '../model/back-light-temperature-setting';
import { BackLightBrightnessSetting } from '../model/back-light-brightness-setting';

function parseStatus(data: AdditionalInfoImpl): AdditionalInfoImpl {
  if (data.hasOwnProperty('device')) {
    const deviceType: DeviceType = data.device.deviceType;
    const classType = getClassType(deviceType);
    if (classType) {
      const status: AdditionalInfoImpl = plainToClass<AdditionalInfoImpl, any>(classType, data);
      return status;
    }
  }
  console.error('Couldn\'t parse incoming status', data);
  return null;
}

export function parseStatuses(data: Array<AdditionalInfoImpl>, mutableFields?: DataSourceField[]): AdditionalInfoImpl[] {
  const statuses: AdditionalInfoImpl[] = [];
  data.forEach(stat => {
    const status = parseStatus(stat);
    if (status) {
      if (mutableFields)
        mutateData(status, mutableFields);
      statuses.push(status);
    }
  });
  return statuses;
}

export function parsePhantomes(data: Record<string, unknown>): GPSPhantomFrame[] {
  return deserializeArray(GPSPhantomFrame, JSON.stringify(data));
}

export function parseStatusesWithMutatorCollection(data: Array<AdditionalInfoImpl>, mutatorFieldsCollection: MutatorFieldsCollection): AdditionalInfoImpl[] {
  const statuses: AdditionalInfoImpl[] = [];
  data.forEach(stat => {
    const status = parseStatus(stat);
    if (status) {
      const mutableFields = mutatorFieldsCollection[status.device.deviceType];
      if (mutableFields)
        mutateData(status, mutableFields);
      statuses.push(status);
    }
  });
  return statuses;
}

function getClassType(deviceType: DeviceType): any {
  switch (deviceType) {
    case DeviceType.CAN1:
      return CAN1Status;
    case DeviceType.CAN2:
      return CAN2Status;
    case DeviceType.CAN3:
      return CAN3Status;
    case DeviceType.GPS:
      return GPSStatus;
    case DeviceType.LCD:
      return LcdStatus;
    case DeviceType.LED:
      return LedStatus;
    case DeviceType.NVR:
      return NVRStatus;
    case DeviceType.SIL:
      return SilStatus;
    case DeviceType.TIC:
      return TicStatus;
    case DeviceType.VICOM:
      return VicomStatus;
    case DeviceType.CUSTOM_CAN:
      return CustomCANStatus;
    case DeviceType.OBU:
      return OBUStatus;
  }
}

export function getSettingsClassType(type: DeviceSettingsType): Type {
  switch (type) {
    case DeviceSettingsType.BACK_LIGHT_COLOR: return BackLightColorSetting;
    case DeviceSettingsType.BACK_LIGHT_TEMPERATURE: return BackLightTemperatureSetting;
    case DeviceSettingsType.BACK_LIGHT_BRIGHTNESS: return BackLightBrightnessSetting;
  }
}

export function parseDeviceSettings<T>(settings: Record<string, any>[]): T[] {
    return settings.map((setting: Record<string, any>) => {
      const type: ClassType<T> = getSettingsClassType(setting.setting_type);
      if (type)
        return plainToClass(type, setting);
      return null;
    });
}

export interface MutatorFieldsCollection {
  [key: string]: DataSourceField[];
}
